import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import {
  Bot,
  RefreshCw,
  Save,
  Check,
  X,
  AlertCircle,
  Phone,
  MessageSquare,
  Settings as SettingsIcon,
  Clock,
  PhoneOff,
} from "lucide-react";
import {
  fetchVoiceAssistants,
  updateVoiceAssistant,
  syncVoiceAssistant,
  syncAllVoiceAssistants,
  testVapiConnection as testVoiceServiceConnection,
  getVoiceOptions,
} from "../../api/settings";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Switch } from "../ui/switch";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Separator } from "../ui/separator";

const VoiceAssistantSettings = () => {
  const token = useSelector((state) => state.auth?.token);
  const [assistants, setAssistants] = useState([]);
  const [voiceOptions, setVoiceOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [syncing, setSyncing] = useState({});
  const [expandedAssistant, setExpandedAssistant] = useState(null);
  const [serviceConnected, setServiceConnected] = useState(null);

  useEffect(() => {
    loadAssistants(true); // Force refresh on mount to get latest data
    loadVoiceOptions();
    checkServiceConnection();
  }, [token]);

  const loadAssistants = async (forceRefresh = false) => {
    try {
      setLoading(true);
      const response = await fetchVoiceAssistants(token, { forceRefresh });
      if (response.success && response.data) {
        setAssistants(response.data);
      }
    } catch (error) {
      console.error("Failed to load voice assistants:", error);
      toast.error("Failed to load voice assistants");
    } finally {
      setLoading(false);
    }
  };

  const loadVoiceOptions = async () => {
    try {
      const response = await getVoiceOptions(token);
      if (response.success && response.data) {
        setVoiceOptions(response.data);
      }
    } catch (error) {
      console.error("Failed to load voice options:", error);
    }
  };

  const checkServiceConnection = async () => {
    try {
      const response = await testVoiceServiceConnection(token);
      setServiceConnected(response.success);
    } catch (error) {
      setServiceConnected(false);
    }
  };

  const handleUpdate = async (assistantId, field, value) => {
    try {
      setSaving((prev) => ({ ...prev, [assistantId]: true }));

      const updateData = { [field]: value };
      const response = await updateVoiceAssistant(token, assistantId, updateData);

      if (response.success) {
        // Update local state
        setAssistants((prev) =>
          prev.map((a) =>
            a.id === assistantId ? { ...a, ...response.data } : a
          )
        );
        toast.success("Assistant updated successfully!");
      }
    } catch (error) {
      console.error("Failed to update assistant:", error);
      toast.error("Failed to update assistant");
    } finally {
      setSaving((prev) => ({ ...prev, [assistantId]: false }));
    }
  };

  const handleSync = async (assistantId) => {
    try {
      setSyncing((prev) => ({ ...prev, [assistantId]: true }));
      const response = await syncVoiceAssistant(token, assistantId);

      if (response.success) {
        toast.success("Assistant synced successfully!");
        await loadAssistants();
      }
    } catch (error) {
      console.error("Failed to sync assistant:", error);
      toast.error("Failed to sync assistant");
    } finally {
      setSyncing((prev) => ({ ...prev, [assistantId]: false }));
    }
  };

  const handleSyncAll = async () => {
    try {
      setSyncing({ all: true });
      const response = await syncAllVoiceAssistants(token);

      if (response.success) {
        toast.success(`Synced ${response.data.synced} assistants!`);
        await loadAssistants();
      }
    } catch (error) {
      console.error("Failed to sync all:", error);
      toast.error("Failed to sync assistants");
    } finally {
      setSyncing({ all: false });
    }
  };

  const getSyncStatusBadge = (status) => {
    const badges = {
      synced: <Badge className="bg-green-500 text-white">Synced</Badge>,
      pending: <Badge className="bg-orange-500 text-white">Pending</Badge>,
      syncing: <Badge className="bg-blue-500 text-white">Syncing...</Badge>,
      error: <Badge className="bg-red-500 text-white">Error</Badge>,
    };
    return badges[status] || badges.pending;
  };

  const getAssistantIcon = (type) => {
    const icons = {
      buyer: "🏡",
      seller: "📞",
      general: "💬",
    };
    return icons[type] || "🤖";
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse">
          <div className="h-32 bg-gray-200 rounded-lg mb-4"></div>
          <div className="h-32 bg-gray-200 rounded-lg mb-4"></div>
          <div className="h-32 bg-gray-200 rounded-lg"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Bot className="w-6 h-6 text-[#55C5D0]" />
            AI Voice Calling System
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Configure AI assistants that make phone calls to leads (not website chat)
          </p>
          <p className="text-xs text-gray-500 mt-1">
            3 assistants: Buyer (property enquiries), Seller (cold calling), General (other calls)
          </p>
        </div>
        <div className="flex gap-2">
          {serviceConnected !== null && (
            <Badge
              className={
                serviceConnected
                  ? "bg-green-100 text-green-800"
                  : "bg-red-100 text-red-800"
              }
            >
              {serviceConnected ? "Service Connected" : "Service Disconnected"}
            </Badge>
          )}
          <Button
            onClick={handleSyncAll}
            disabled={syncing.all}
            className="bg-[#55C5D0] hover:bg-[#3bb3be] text-white"
          >
            <RefreshCw
              className={`w-4 h-4 mr-2 ${syncing.all ? "animate-spin" : ""}`}
            />
            Sync All Assistants
          </Button>
        </div>
      </div>

      {/* Assistants */}
      <div className="space-y-4">
        {assistants.map((assistant) => (
          <Card key={assistant.id} className="border-2 hover:border-[#55C5D0] transition-colors">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="text-3xl">{getAssistantIcon(assistant.assistant_type)}</div>
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      {assistant.name}
                      {getSyncStatusBadge(assistant.sync_status)}
                    </CardTitle>
                    <CardDescription className="mt-1">
                      {assistant.assistant_type_display}
                    </CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={assistant.is_active}
                    onCheckedChange={(checked) =>
                      handleUpdate(assistant.id, "is_active", checked)
                    }
                    disabled={saving[assistant.id]}
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      setExpandedAssistant(
                        expandedAssistant === assistant.id ? null : assistant.id
                      )
                    }
                  >
                    {expandedAssistant === assistant.id ? "Hide" : "Edit"}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleSync(assistant.id)}
                    disabled={syncing[assistant.id]}
                    className="bg-[#55C5D0] hover:bg-[#3bb3be] text-white"
                  >
                    <RefreshCw
                      className={`w-4 h-4 ${
                        syncing[assistant.id] ? "animate-spin" : ""
                      }`}
                    />
                  </Button>
                </div>
              </div>
            </CardHeader>

            {expandedAssistant === assistant.id && (
              <CardContent className="space-y-6 pt-6 border-t">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-700 font-medium">Description</Label>
                    <Input
                      value={assistant.description || ""}
                      onChange={(e) =>
                        handleUpdate(assistant.id, "description", e.target.value)
                      }
                      placeholder="Assistant description..."
                      disabled={saving[assistant.id]}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700 font-medium">Voice</Label>
                    <Select
                      value={assistant.voice_id}
                      onValueChange={(value) =>
                        handleUpdate(assistant.id, "voice_id", value)
                      }
                      disabled={saving[assistant.id]}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {voiceOptions?.voices?.map(([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* First Message */}
                <div>
                  <Label className="text-gray-700 font-medium flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-[#55C5D0]" />
                    Opening Greeting
                  </Label>
                  <textarea
                    value={assistant.first_message || ""}
                    onChange={(e) =>
                      handleUpdate(assistant.id, "first_message", e.target.value)
                    }
                    placeholder="Hi, this is Alex from Collings Real Estate..."
                    disabled={saving[assistant.id]}
                    rows={2}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                  />
                </div>

                {/* System Prompt */}
                <div>
                  <Label className="text-gray-700 font-medium flex items-center gap-2">
                    <Bot className="w-4 h-4 text-[#55C5D0]" />
                    AI Behavior & Personality
                  </Label>
                  <textarea
                    value={assistant.system_prompt || ""}
                    onChange={(e) =>
                      handleUpdate(assistant.id, "system_prompt", e.target.value)
                    }
                    placeholder="You are a friendly assistant..."
                    disabled={saving[assistant.id]}
                    rows={6}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent font-mono text-sm"
                  />
                </div>

                {/* Call Settings */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-700 font-medium flex items-center gap-2">
                      <Clock className="w-4 h-4 text-[#55C5D0]" />
                      Max Duration (seconds)
                    </Label>
                    <Input
                      type="number"
                      value={assistant.max_duration_seconds || 300}
                      onChange={(e) =>
                        handleUpdate(
                          assistant.id,
                          "max_duration_seconds",
                          parseInt(e.target.value)
                        )
                      }
                      disabled={saving[assistant.id]}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700 font-medium flex items-center gap-2">
                      <Phone className="w-4 h-4 text-[#55C5D0]" />
                      Voicemail Detection
                    </Label>
                    <div className="flex items-center gap-2 mt-2">
                      <Switch
                        checked={assistant.voicemail_detection}
                        onCheckedChange={(checked) =>
                          handleUpdate(
                            assistant.id,
                            "voicemail_detection",
                            checked
                          )
                        }
                        disabled={saving[assistant.id]}
                      />
                      <span className="text-sm text-gray-600">
                        {assistant.voicemail_detection ? "Enabled" : "Disabled"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Voicemail Message */}
                {assistant.voicemail_detection && (
                  <div>
                    <Label className="text-gray-700 font-medium">
                      Voicemail Message
                    </Label>
                    <textarea
                      value={assistant.voicemail_message || ""}
                      onChange={(e) =>
                        handleUpdate(
                          assistant.id,
                          "voicemail_message",
                          e.target.value
                        )
                      }
                      placeholder="Hi, this is Alex. I'll call back later..."
                      disabled={saving[assistant.id]}
                      rows={2}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                    />
                  </div>
                )}

                {/* Transfer Settings */}
                <div>
                  <Label className="text-gray-700 font-medium flex items-center gap-2 mb-2">
                    <PhoneOff className="w-4 h-4 text-[#55C5D0]" />
                    Enable Call Transfer
                  </Label>
                  <Switch
                    checked={assistant.enable_transfer}
                    onCheckedChange={(checked) =>
                      handleUpdate(assistant.id, "enable_transfer", checked)
                    }
                    disabled={saving[assistant.id]}
                  />
                </div>

                {/* Sync Info */}
                {assistant.last_synced_at && (
                  <div className="text-sm text-gray-500 pt-4 border-t">
                    Last synced:{" "}
                    {new Date(assistant.last_synced_at).toLocaleString()}
                    {assistant.sync_error && (
                      <div className="text-red-600 mt-1 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {assistant.sync_error}
                      </div>
                    )}
                  </div>
                )}

                {saving[assistant.id] && (
                  <div className="flex items-center gap-2 text-[#55C5D0]">
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span className="text-sm">Saving changes...</span>
                  </div>
                )}
              </CardContent>
            )}
          </Card>
        ))}
      </div>

      {assistants.length === 0 && !loading && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Bot className="w-16 h-16 text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No Voice Assistants Configured
            </h3>
            <p className="text-sm text-gray-600 text-center max-w-md mb-4">
              Voice assistants help automate phone calls to leads. Contact your system administrator to set up voice calling assistants.
            </p>
            <Button
              onClick={() => loadAssistants(true)}
              variant="outline"
              className="mt-2"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default VoiceAssistantSettings;

